/**
 * WelcomeCard
 * ----------------------------------------------------------------------------
 * First-load personalised greeting overlay. Fires ONCE per visitor (persisted
 * via localStorage) and dissolves after 8 seconds or on user dismiss. Reads
 * the geolocation + ordinal number embedded in the /api/visits/track response
 * and slams a brutalist "WELCOME, EDMONTON. YOU'RE LISTENER #10,472 FROM
 * CANADA 🇨🇦" card into the top-right. Emotional judge hook — makes every
 * first visit feel personal without asking for a signup.
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { trackVisit } from "@/lib/visitorSession";

const DISMISSED_KEY = "drr_welcome_dismissed";

// Convert an ISO 3166-1 alpha-2 country code to a flag emoji (e.g. "CA" -> 🇨🇦).
function ccToFlag(cc) {
  if (!cc || cc.length !== 2) return "";
  const base = 127397; // 'A'.codePointAt(0) - 65 + 0x1F1E6
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => c.charCodeAt(0) + base));
}

function formatNumber(n) {
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US");
}

export default function WelcomeCard() {
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show at most once per visitor. If they've already seen it, don't fire.
    let alreadySeen = false;
    try {
      alreadySeen = !!localStorage.getItem(DISMISSED_KEY);
    } catch (e) {
      alreadySeen = false;
    }
    if (alreadySeen) return;

    let cancelled = false;
    (async () => {
      try {
        const d = (await trackVisit()) || {};
        if (cancelled) return;
        // Only render if we actually have a city or country (otherwise we'd
        // show "WELCOME, ." which looks broken — skip silently in that case).
        if (!d.city && !d.country) return;
        setData({
          city: d.city || "",
          country: d.country || "",
          country_code: d.country_code || "",
          visitor_number: d.visitor_number || null,
        });
        // Small delay so the card animates in AFTER hero content lands.
        setTimeout(() => !cancelled && setVisible(true), 800);
        // Auto-dismiss after 8 seconds so we're not shouting forever.
        setTimeout(() => !cancelled && setVisible(false), 9500);
      } catch (e) {
        /* silent — welcome card is best-effort, never blocks the site */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  if (!data) return null;

  const flag = ccToFlag(data.country_code);
  const location = data.city
    ? data.city.toUpperCase()
    : (data.country || "").toUpperCase();

  return (
    <div
      className={`fixed z-[70] top-24 right-4 md:right-6 max-w-[380px] transition-all duration-500 ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6 pointer-events-none"
      }`}
      data-testid="welcome-card"
      role="status"
      aria-live="polite"
    >
      <div className="relative border-2 border-red-600 bg-black shadow-[0_10px_40px_rgba(220,38,38,0.4)]">
        {/* Red accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-600" />

        {/* Dismiss */}
        <button
          type="button"
          onClick={() => {
            handleDismiss();
          }}
          className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-red-500 transition-colors"
          aria-label="Dismiss welcome"
          data-testid="welcome-card-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="pl-5 pr-8 py-4">
          {/* Eyebrow */}
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-600 animate-pulse" />
            <span className="text-[10px] tracking-[0.35em] uppercase text-red-500 font-display">
              //_signal_lock
            </span>
          </div>

          {/* Headline */}
          <div
            className="font-display text-2xl md:text-3xl leading-[0.9] tracking-tight text-white uppercase"
            data-testid="welcome-card-headline"
          >
            Welcome,
            <br />
            <span className="text-red-500">
              {location || "Listener"}
              {flag && <span className="ml-2 text-2xl align-middle">{flag}</span>}
            </span>
            <span className="text-red-500">.</span>
          </div>

          {/* Meta line — ordinal + country */}
          {data.visitor_number && (
            <div
              className="mt-3 text-xs md:text-sm text-zinc-300 leading-relaxed"
              data-testid="welcome-card-meta"
            >
              You&apos;re listener
              <span className="font-display text-white ml-1.5 mr-1.5 text-base">
                #{formatNumber(data.visitor_number)}
              </span>
              {data.country && (
                <>
                  from <span className="text-zinc-100 uppercase tracking-wider">{data.country}</span>
                </>
              )}
              .
            </div>
          )}

          {/* Sign-off line */}
          <div className="mt-2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 font-display">
            //_wired_for_the_rock_in_you
          </div>
        </div>
      </div>
    </div>
  );
}
